<!-- Schema defined in utils/schema/cover-letter-schema.js -->
<script setup>
  import { resolveMotivationQuestions } from '~/utils/employer'

  const props = defineProps({
    errors: { type: Object, required: true },
    values: { type: Object, required: true },
    required: { type: Boolean, required: true },
    job: { type: Object, required: true },
    submitCount: { type: Number, default: 0 },
    linkColor: { type: String, default: '' },
    showOnlyQuestions: { type: Boolean, default: false },
    questionsIntro: { type: String, default: '' },
    questionsSelected: { type: Boolean, default: false },
    hideToggle: { type: Boolean, default: false }
  })
  const { user } = useUser()

  const letterMode = computed(() => props.job.letterMode)
  const fileRequired = computed(() => props.required && letterMode.value === 'file')
  const questionsRequired = computed(() => props.questionsSelected || (props.required && letterMode.value === 'questions'))
  const toggleLabel = computed(() =>
    questionsRequired.value ? 'Veuillez répondre aux questions de motivation de l’institution' : 'Répondre aux questions de motivation'
  )

  const questions = computed(() => resolveMotivationQuestions(props.job.employer))

  const letterItems = computed(() =>
    questions.value.map((question, index) => ({ question, answer: props.values[`motivationQuestion${index + 1}`] || '' }))
  )

  const hasQuestionsErrors = computed(() => {
    if (!props.submitCount) return false
    return questions.value.some((_, index) => props.errors[`motivationQuestion${index + 1}`])
  })

  const generatedLetterRef = ref(null)
  const showQuestions = ref(letterMode.value === 'questions')
  const questionsVisible = computed(() => props.hideToggle || showQuestions.value)

  watch(hasQuestionsErrors, hasErrors => {
    if (hasErrors) showQuestions.value = true
  })

  watch(
    () => props.questionsSelected,
    selected => {
      if (selected) showQuestions.value = true
    }
  )

  const canGenerateLetter = () => {
    return generatedLetterRef.value.canGenerateLetter()
  }
  const getGeneratedLetterPdf = () => {
    return generatedLetterRef.value.getGeneratedLetterPdf()
  }
  defineExpose({ getGeneratedLetterPdf, canGenerateLetter })
</script>

<template>
  <div>
    <FormField
      class="basis-full"
      id="letter"
      v-if="letterMode !== 'questions' && !showOnlyQuestions"
      accept="application/pdf"
      :error="props.errors.letter"
      label="Lettre de motivation (format PDF)"
      name="letter"
      :required="fileRequired"
      type="file"
    />
    <template v-if="letterMode !== 'file'">
      <div
        class="my-3 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-zinc-400"
        v-if="letterMode === 'both' && !showOnlyQuestions"
      >
        <span class="h-px flex-1 bg-zinc-200" />
        ou
        <span class="h-px flex-1 bg-zinc-200" />
      </div>

      <p class="mb-1 font-bold" v-if="!showOnlyQuestions">Mot de motivation <span class="ml-0.5 text-zinc-400">*</span>&nbsp;:</p>

      <div class="overflow-hidden rounded-lg border border-zinc-300 bg-zinc-50/70">
        <button
          class="flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left transition-colors hover:bg-zinc-100/70"
          v-if="!hideToggle"
          type="button"
          @click="showQuestions = !showQuestions"
        >
          <span class="flex items-center gap-2.5 text-sm font-medium">
            <Icon :class="{ 'text-red-500': hasQuestionsErrors, 'text-zinc-400': !hasQuestionsErrors }" name="pen" />
            <span :class="{ 'text-red-500': hasQuestionsErrors }"
              >{{ toggleLabel }}<span class="ml-0.5 text-zinc-400" v-if="questionsRequired">*</span></span
            >
          </span>
          <Icon class="shrink-0 text-zinc-400 transition-transform duration-200" name="chevron-down" :class="{ 'rotate-180': showQuestions }" />
        </button>
        <Transition>
          <div class="space-y-4 bg-white px-3.5 py-4" v-show="questionsVisible" :class="{ 'border-t border-zinc-300': !hideToggle }">
            <div v-for="(question, index) in questions" :key="index">
              <label class="mb-1.5 block text-sm font-medium" :for="`motivationQuestion${index + 1}`">{{ question }}</label>
              <FormField
                :id="`motivationQuestion${index + 1}`"
                :error="props.errors[`motivationQuestion${index + 1}`]"
                :name="`motivationQuestion${index + 1}`"
                :required="questionsRequired"
                rows="3"
                type="textarea"
              />
            </div>
          </div>
        </Transition>
      </div>
    </template>

    <FormGeneratedLetter
      v-if="!showOnlyQuestions"
      ref="generatedLetterRef"
      :candidate-first-name="user?.firstName || values.firstName"
      :candidate-last-name="user?.lastName || values.lastName"
      :speaker="job.speaker"
      :employer-name="props.job.employer.name"
      :items="letterItems"
    />
  </div>
</template>
